import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  Search,
  User,
  Heart,
  Menu,
  X,
  Phone,
  Globe,
  ChevronDown,
  ArrowLeftRight,
} from 'lucide-react';
import { useCartStore } from '../store/useCartStore';
import { useWishlistStore } from '../store/useWishlistStore';
import { useAuthStore, getUserDisplayName } from '../store/useAuthStore';
import CartDrawer from './CartDrawer';
import SearchDropdown from './SearchDropdown';
import AuthModal from './AuthModal';
import { useScrollVisibility } from '../hooks/useScrollVisibility';
import { fetchParentCategories, type Category } from '../utils/apiHelpers';
import { getCachedData } from '../utils/requestCache';

const navLinks = [
  {
    label: 'Home',
    to: '/',
    // children: [
    //   { to: '/', label: 'Homepage' },
    //   { to: '/about', label: 'About Us' },
    // ],
  },
  {
    label: 'Shop',
    to: '/shop',
    // children: [
    //   { to: '/shop', label: 'All Products' },
    //   { to: '/shop', label: 'On Sale' },
    // ],
  },
  {
    label: 'About',
    to: '/about',
    // children: [
    //   { to: '/about', label: 'Our Story' },
    //   { to: '/team', label: 'Our Team' },
    // ],
  },
  {
    label: 'Contact',
    to: '/contact',
    // children: [
    //   { to: '/about', label: 'About' },
    //   { to: '/contact', label: 'Contact' },
    //   { to: '/faqs', label: 'FAQs' },
    //   { to: '/track', label: 'Track Order' },
    // ],
  },
];

export default function Header() {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const headerVisible = useScrollVisibility(10);
  const items = useCartStore((s) => s.items);
  const toggleCart = useCartStore((s) => s.toggleCart);
  const wishlistItems = useWishlistStore((s) => s.items);
  const user = useAuthStore((s) => s.user);
  const displayName = getUserDisplayName(user);
  const navigate = useNavigate();

  const cartCount = items.reduce((sum, i) => sum + i.quantity, 0);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedData<Category[]>('categories:parents');
    if (cached && cached.length > 0) {
      setCategories(cached.slice(0, 10));
    }

    fetchParentCategories()
      .then((list) => {
        if (!cancelled) setCategories(list);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (selectedCategory) {
      params.set('category', selectedCategory);
      params.set('include_sub', 'true');
    }
    navigate(`/shop${params.toString() ? `?${params}` : ''}`);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (value) {
      params.set('category', value);
      params.set('include_sub', 'true');
    }
    navigate(`/shop${params.toString() ? `?${params}` : ''}`);
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 bg-white transition-transform duration-300 ${
          headerVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        {/* Top utility bar */}
        <div className="hidden lg:flex items-center justify-between border-b border-[var(--border-light)] bg-[var(--header-top-bg)] px-6 py-2.5 text-sm text-[var(--text-muted)]">
          <div>
            Call us for free:{' '}
            <a href="tel:+923224540783" className="font-semibold text-[var(--primary-red)] hover:underline">
              +92 322 4540783
            </a>
          </div>
          <div className="flex items-center gap-6">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 hover:text-[var(--foreground)]"
            >
              <Globe className="h-4 w-4" />
              Language: English
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                if (user) {
                  navigate('/account');
                } else {
                  setIsAuthModalOpen(true);
                }
              }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--foreground)] transition hover:text-[var(--foreground)]"
            >
              <User className="h-4 w-4" />
              {displayName ?? 'Sign In'}
            </button>
          </div>
        </div>

        {/* Main header row */}
        <div className="border-b border-[var(--border-light)] px-4 py-4 md:px-6">
          <div className="mx-auto flex max-w-7xl items-center gap-4 lg:gap-8">
            <Link to="/" className="shrink-0">
              <img
                src="/dehleze-logo.png"
                alt="Dehleze"
                className="h-10 w-auto object-contain md:h-12"
              />
            </Link>

            <form
              onSubmit={handleSearch}
              className="hidden flex-1 items-stretch overflow-hidden rounded-full border border-[var(--border-light)] md:flex"
            >
              <div className="relative flex shrink-0 items-center border-r border-[var(--border-light)] bg-white">
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="cursor-pointer appearance-none bg-transparent py-3 pl-4 pr-8 text-sm text-[var(--foreground)] outline-none"
                  aria-label="All categories"
                >
                  <option value="">All categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 h-4 w-4 text-[var(--text-muted)]" />
              </div>
              <div className="relative flex flex-1">
                <input
                  type="search"
                  placeholder="Search for products"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    if (e.target.value.trim()) {
                      setIsSearchOpen(true);
                    } else {
                      setIsSearchOpen(false);
                    }
                  }}
                  onFocus={() => query.trim() && setIsSearchOpen(true)}
                  onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)}
                  className="w-full bg-white px-4 py-3 text-sm text-[var(--foreground)] outline-none placeholder:text-gray-400"
                />
                {isSearchOpen && query && (
                  <div className="absolute inset-x-0 top-full z-30 mt-1 rounded-lg border border-[var(--border-light)] bg-white shadow-xl">
                    <SearchDropdown query={query} onClose={() => setIsSearchOpen(false)} />
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="flex shrink-0 items-center justify-center bg-[var(--primary-red)] px-5 text-white transition hover:bg-[var(--primary-red-hover)]"
                aria-label="Search"
              >
                <Search className="h-5 w-5" />
              </button>
            </form>

            <div className="hidden shrink-0 items-center gap-3 lg:flex">
              
              
             
              <div className="text-sm leading-snug">
                <p className="text-[var(--text-muted)]">
                  Call us now:{' '}
                  <a href="tel:+923224540783" className="font-semibold text-[var(--primary-red)]">
                    +92 322 4540783
                  </a>
                </p>
                <p className="text-[var(--text-muted)]">
                  Email:{' '}
                  <a href="mailto:dehleze.pk@gmail.com" className="font-semibold text-[#fd9e10]">
                    dehleze.pk@gmail.com
                  </a>
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((o) => !o)}
              className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--border-light)] md:hidden"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Mobile search */}
          <form
            onSubmit={handleSearch}
            className="relative mx-auto mt-3 flex max-w-7xl overflow-hidden rounded-full border border-[var(--border-light)] md:hidden"
          >
            <input
              type="search"
              placeholder="Search for products"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (e.target.value.trim()) {
                  setIsSearchOpen(true);
                } else {
                  setIsSearchOpen(false);
                }
              }}
              onFocus={() => query.trim() && setIsSearchOpen(true)}
              onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)}
              className="flex-1 bg-white px-4 py-2.5 text-sm outline-none"
            />
            <button type="submit" className="bg-[var(--primary-red)] px-4 text-white">
              <Search className="h-4 w-4" />
            </button>
            {isSearchOpen && query && (
              <div className="absolute inset-x-0 top-full z-30 mt-1 rounded-lg border border-[var(--border-light)] bg-white shadow-xl">
                <SearchDropdown query={query} onClose={() => setIsSearchOpen(false)} />
              </div>
            )}
          </form>
        </div>

        {/* Navigation bar */}
        <div className="border-b border-[var(--border-light)] px-4 md:px-6">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
            <nav className="hidden flex-1 items-center gap-1 lg:flex">
              {navLinks.map((link) => (
                <div key={link.label} className="group relative">
                  <NavLink
                    to={link.to}
                    end={link.to === '/'}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-0.5 px-4 py-3.5 text-sm font-medium transition ${
                        isActive
                          ? 'text-[var(--primary-red)]'
                          : 'text-[var(--foreground)] hover:text-[var(--primary-red)]'
                      }`
                    }
                  >
                    {link.label}
                    <span className="text-[var(--primary-red)]"></span>
                  </NavLink>
                  {/* Dropdown disabled - children not needed */}
                </div>
              ))}
            </nav>

            <div className="flex min-w-max flex-wrap items-center justify-end gap-1.5 py-2 lg:gap-4 lg:py-0">
              <Link
                to="https://dehleze.store/vendor/login/"
                className="inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-full bg-[var(--primary-red)] px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[var(--primary-red-hover)] sm:px-3 sm:py-2 sm:text-sm"
              >
                Become Saler
              </Link>
              <Link
                to="/shop"
                className="relative inline-flex h-8 w-8 items-center justify-center text-[var(--foreground)] hover:text-[var(--primary-red)] sm:h-10 sm:w-10"
                aria-label="Compare"
              >
                <ArrowLeftRight className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[var(--primary-red)] px-1 text-[10px] font-bold text-white">
                  0
                </span>
              </Link>
              <Link
                to="/wishlist"
                className="relative inline-flex h-8 w-8 items-center justify-center text-[var(--foreground)] hover:text-[var(--primary-red)] sm:h-10 sm:w-10"
                aria-label="Wishlist"
              >
                <Heart className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[var(--primary-red)] px-1 text-[10px] font-bold text-white">
                  {wishlistItems.length}
                </span>
              </Link>
              <button
                type="button"
                onClick={() => toggleCart(true)}
                className="relative inline-flex h-8 w-8 items-center justify-center text-[var(--foreground)] hover:text-[var(--primary-red)] sm:h-10 sm:w-10"
                aria-label="Cart"
              >
                <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[var(--primary-red)] px-1 text-[10px] font-bold text-white">
                  {cartCount}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="border-b border-[var(--border-light)] bg-white px-4 py-4 shadow-sm lg:hidden">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="rounded-2xl px-4 py-3 text-sm font-medium text-[var(--foreground)] transition hover:bg-[var(--background)] hover:text-[var(--primary-red)]"
                >
                  {link.label}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsAuthModalOpen(true);
                }}
                className="w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-[var(--foreground)] transition hover:bg-[var(--background)] hover:text-[var(--primary-red)]"
              >
                {displayName ?? 'Sign In'}
              </button>
              <Link
                to="/work-with-us"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center rounded-full bg-[var(--primary-red)] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--primary-red-hover)]"
              >
                Become A Saler
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-2 rounded-2xl px-4 py-3 text-sm text-[var(--text-muted)] transition hover:bg-[var(--background)] hover:text-[var(--foreground)]"
              >
                <Phone className="h-4 w-4" />
                +92 322 4540783
              </Link>
            </nav>
          </div>
        )}
      </header>
      <AuthModal open={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      <CartDrawer />
    </>
  );
}
